import type { HttpError } from '@refinedev/core'
import { CanAccess, useCan } from '@refinedev/core'
import { useTable } from '@refinedev/react-table'
import type { Location } from '@sirius/contracts/admin'
import type { ColumnDef } from '@tanstack/react-table'
import { Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from '~/components/confirmDialog'
import { ListEmptyState } from '~/components/listEmptyState'
import { EditButton } from '~/components/refineUi/buttons/edit'
import { ShowButton } from '~/components/refineUi/buttons/show'
import { DataTable } from '~/components/refineUi/dataTable/dataTable'
import { DataTableFilterDropdownText } from '~/components/refineUi/dataTable/dataTableFilter'
import { DataTableSorter } from '~/components/refineUi/dataTable/dataTableSorter'
import { ListView, ListViewHeader } from '~/components/refineUi/views/listView'
import { Button } from '~/components/ui/button'
import { formatDateTime } from '~/i18n/format'
import { useDeleteLocation } from './locationDelete'

const dateTime = (value: Date) => formatDateTime(value)

export function LocationList() {
  const { t } = useTranslation()
  const deleteLocation = useDeleteLocation()
  const columns = useMemo<ColumnDef<Location>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('locationsPage.list.name')}
            <DataTableSorter column={column} />
            <DataTableFilterDropdownText
              defaultOperator="contains"
              operators={['contains']}
              column={column}
              table={table}
              placeholder={t('locationsPage.list.searchName')}
            />
          </div>
        ),
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            {t('locationsPage.list.created')}
            <DataTableSorter column={column} />
          </div>
        ),
        cell: ({ getValue }) => dateTime(getValue<Date>()),
        enableColumnFilter: false,
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: t('locationsPage.list.updated'),
        cell: ({ getValue }) => dateTime(getValue<Date>()),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <ShowButton
              recordItemId={row.original.id}
              size="sm"
            />
            <EditButton
              recordItemId={row.original.id}
              size="sm"
            />
            {/* a technician only reads locations */}
            <CanAccess
              resource="locations"
              action="delete"
            >
              <ConfirmDialog
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('locationsPage.list.deleteAria', {
                      name: row.original.name,
                    })}
                  >
                    <Trash2 />
                  </Button>
                }
                title={t('locationsPage.list.deleteTitle', {
                  name: row.original.name,
                })}
                description={t('locationsPage.list.deleteDescription')}
                confirmLabel={t('locationsPage.list.delete')}
                destructive
                onConfirm={() => deleteLocation(row.original.id)}
              />
            </CanAccess>
          </div>
        ),
      },
    ],
    [deleteLocation, t],
  )

  const table = useTable<Location, HttpError>({
    columns,
    refineCoreProps: {
      resource: 'locations',
      syncWithLocation: true,
      pagination: { pageSize: 20 },
      sorters: { initial: [{ field: 'name', order: 'asc' }] },
    },
  })
  const { tableQuery, filters } = table.refineCore
  const { data: createAccess } = useCan({
    resource: 'locations',
    action: 'create',
  })
  const isEmpty = tableQuery.data?.total === 0

  return (
    <ListView>
      <ListViewHeader
        title={t('locations.locations')}
        canCreate={createAccess?.can === true}
      />
      {isEmpty ? (
        <ListEmptyState
          resource="locations"
          emptyText={t('locationsPage.list.empty')}
          noMatchText={t('locationsPage.list.noMatch')}
          hasFilters={filters.length > 0}
          // via the column filters, so react-table and Refine stay in sync
          onClearFilters={() => table.reactTable.setColumnFilters([])}
        />
      ) : (
        <DataTable table={table} />
      )}
    </ListView>
  )
}
